/**
 * Trích xuất & map thông báo lỗi từ response API.
 * Backend (Laravel) có thể trả nhiều dạng: { msg }, { message }, { error: "..." }
 * hoặc { error: { field: "message" } }. Hàm gom lại thành 1 chuỗi để hiển thị.
 * Khi lang === 'vi', các message tiếng Anh phổ biến được map sang tiếng Việt;
 * ngôn ngữ khác (hoặc mẫu không khớp) giữ nguyên message gốc để không mất thông tin.
 */

// Nhãn tiếng Việt cho các field xuất hiện trong message lỗi backend.
// Laravel mặc định dùng tên attribute có dấu cách (vd "display name"); map cả dạng gạch dưới cho chắc.
const FIELD_LABELS_VI: Record<string, string> = {
    "display name": "Tên khách hàng",
    "display_name": "Tên khách hàng",
    "phone": "Số điện thoại",
    "email": "Email",
    "address": "Địa chỉ",
    "birthday": "Ngày sinh",
    "gender": "Giới tính",
    "province code": "Tỉnh/Thành phố",
    "province_code": "Tỉnh/Thành phố",
    "ward code": "Phường/Xã",
    "ward_code": "Phường/Xã",
    "note": "Ghi chú",
};

const labelVi = (field: string): string => {
    const key = (field || "").toLowerCase().trim();
    return FIELD_LABELS_VI[key] || key;
};

// Map thủ công các mẫu message lỗi (Laravel) sang tiếng Việt. Không khớp mẫu -> giữ nguyên message gốc.
const MESSAGE_RULES_VI: { re: RegExp; build: (m: RegExpMatchArray) => string }[] = [
    { re: /^the (.+?) field is required\.?$/i, build: (m) => `${labelVi(m[1])} là bắt buộc` },
    { re: /^the (.+?) has already been taken\.?$/i, build: (m) => `${labelVi(m[1])} đã tồn tại` },
    { re: /^the (.+?)(?: field)? must be a valid email(?: address)?\.?$/i, build: (m) => `${labelVi(m[1])} không đúng định dạng email` },
    { re: /^the (.+?) format is invalid\.?$/i, build: (m) => `${labelVi(m[1])} không đúng định dạng` },
    { re: /^the (.+?) must be at least (\d+) characters\.?$/i, build: (m) => `${labelVi(m[1])} phải có ít nhất ${m[2]} ký tự` },
    { re: /^the (.+?) (?:may not be|must not be) greater than (\d+) characters\.?$/i, build: (m) => `${labelVi(m[1])} không được vượt quá ${m[2]} ký tự` },
    { re: /^the (.+?) must be a number\.?$/i, build: (m) => `${labelVi(m[1])} phải là số` },
];

export const translateApiMessageVi = (msg: string): string => {
    const raw = (msg || "").trim();
    for (const rule of MESSAGE_RULES_VI) {
        const m = raw.match(rule.re);
        if (m) return rule.build(m);
    }
    return raw;
};

// Backend chỉ trả message 1 ngôn ngữ. Một số message nghiệp vụ trả thuần tiếng Việt
// -> map sang tiếng Anh khi giao diện đang chọn EN. Không khớp mẫu thì giữ nguyên.
const MESSAGE_RULES_EN: { re: RegExp; build: (m: RegExpMatchArray) => string }[] = [
    { re: /^không thể xóa khách hàng vì đã có lịch hẹn trong hệ thống\.?$/i, build: () => "Cannot delete this customer because they already have appointments in the system" },
    { re: /^xóa khách hàng thành công\.?$/i, build: () => "Customer deleted successfully" },
];

export const translateApiMessageEn = (msg: string): string => {
    const raw = (msg || "").trim();
    for (const rule of MESSAGE_RULES_EN) {
        const m = raw.match(rule.re);
        if (m) return rule.build(m);
    }
    return raw;
};

// Gom message từ 1 giá trị bất kỳ: chuỗi, mảng hoặc object { field: message }
const collectMessages = (value: any): string[] => {
    if (!value) return [];
    if (typeof value === "string") return value.trim() ? [value.trim()] : [];
    if (Array.isArray(value)) return value.flatMap(collectMessages);
    if (typeof value === "object") return Object.values(value).flatMap(collectMessages);
    return [];
};

export const extractApiError = (res: any, lang?: string): string => {
    if (!res) return "";
    // Thứ tự ưu tiên các vị trí backend có thể đặt lỗi
    const sources = [res.error, res.errors, res.msg, res.message, res.data?.error, res.data?.errors, res.data?.msg, res.data?.message];
    let msgs: string[] = [];
    for (const s of sources) {
        msgs = collectMessages(s);
        if (msgs.length) break;
    }
    if (lang?.startsWith("vi")) msgs = msgs.map(translateApiMessageVi);
    else if (lang?.startsWith("en")) msgs = msgs.map(translateApiMessageEn);
    return msgs.join("\n");
};

// Kiểm tra lỗi trả về có phải do thiếu một field cụ thể không (vd "address")
export const isFieldRequiredError = (res: any, field: string): boolean => {
    const errObj = res?.error ?? res?.errors ?? res?.data?.error ?? res?.data?.errors;
    if (errObj && typeof errObj === "object" && field in errObj) return true;
    const text = collectMessages(errObj).join("\n") || extractApiError(res);
    return new RegExp(`the ${field.replace(/_/g, "[ _]")} field is required`, "i").test(text);
};
